import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { mapFlowStateComponentsToDrafts } from '@api/chat/utilities/helpers';
import { createNarrative, readDraftTopics } from '@api/narratives';
import { narrativeErrorStatuses, narrativeStatus } from '@helpers';
import { toast } from '@utilities';

import Footer from '../../../../../../components/Footer';
import PreBuildModal from '../../../../components/PreBuildModal';
import NarrativeFlowView from '../../../../components/NarrativeFlowView';
import NarrativeTitle from '../../../../components/NarrativeFlowView/components/NarrativeTitle';
import NarrativeTopicCard from '../../../../components/NarrativeFlowView/components/NarrativeTopicCard';
import {
  getPromptPillTooltip,
  getPromptPillValue,
  transformPromptSelections,
} from '../../../Narratives/utilities/helpers';

import CanvasFloatingToolbar from './components/CanvasFloatingToolbar';

import {
  areRequiredPromptsFilled,
  buildDraftFlowData,
  buildDraftTopicData,
  buildEditablePromptsFromFlowState,
  buildTopicShell,
  EMPTY_DOWNLOAD_MAP,
  extractEditablePromptAnswers,
  hasPromptRequirements,
} from '../../utilities/helpers';

import styles from './_index.module.scss';

const COMPLETED_OR_ERROR_FLOW_STATUSES = [narrativeStatus.COMPLETED, ...narrativeErrorStatuses];

const buildPromptAnswersPayload = (transformedAnswersMap = {}, readOnlyPromptAnswers = {}) => {
  const editableAnswers = Object.values(transformedAnswersMap)
    .flat()
    .reduce((acc, answer) => {
      if (!answer?.field) return acc;

      const { field, values } = answer;
      if (Array.isArray(values)) {
        acc[field] = acc[field] ? [...acc[field], ...values] : [...values];
      } else if (values && typeof values === 'object' && values.date_range) {
        acc[field] = values.date_range;
      }

      return acc;
    }, {});

  const mergedAnswers = { ...(readOnlyPromptAnswers || {}) };

  Object.entries(editableAnswers).forEach(([field, values]) => {
    if (Array.isArray(values) && Array.isArray(mergedAnswers[field])) {
      mergedAnswers[field] = [...mergedAnswers[field], ...values];
    } else {
      mergedAnswers[field] = values;
    }
  });

  return mergedAnswers;
};

const DEFAULT_PROMPT_MODAL_NAME = 'Narrative prompts';
const DEFAULT_PROMPT_ROUTE_LABEL = 'Selected prompts';

const NarrativesCanvas = ({ flowId, flowState }) => {
  const [activePrompt, setActivePrompt] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [transformedAnswersMap, setTransformedAnswersMap] = useState({});
  const [promptModalName, setPromptModalName] = useState(DEFAULT_PROMPT_MODAL_NAME);
  const promptSelectionsSnapshotRef = useRef(null);
  const shouldPersistPromptChangesRef = useRef(false);

  const prompts = useMemo(() => buildEditablePromptsFromFlowState(flowState), [flowState]);
  const canSelectPrompts = hasPromptRequirements(flowState) && prompts.length > 0;
  const narrativeFlow = useSelector((state) => {
    if (!flowId) return null;
    const flows = state?.websocket?.flows;
    if (!Array.isArray(flows)) return null;
    return flows.find((flow) => Number(flow?.id) === Number(flowId)) || null;
  });

  useEffect(() => {
    if (!flowState || !prompts.length) return;

    const hydratedAnswers = extractEditablePromptAnswers(flowState);
    setSelectedAnswers(hydratedAnswers);

    const initialTransformedAnswers = prompts.reduce((acc, prompt) => {
      const answers = hydratedAnswers?.[prompt.promptKey];
      const hasAnswers = Array.isArray(answers) ? answers.length > 0 : Boolean(answers);
      if (!hasAnswers) return acc;

      try {
        acc[prompt.promptKey] = transformPromptSelections({ answers, prompt });
      } catch (error) {
        console.error('Failed to transform prompt answers from chat flow state:', error);
      }

      return acc;
    }, {});

    setTransformedAnswersMap(initialTransformedAnswers);
    setActivePrompt((current) => {
      if (current && prompts.some((prompt) => prompt.promptKey === current.promptKey)) {
        return current;
      }
      return prompts[0] || null;
    });
    setPromptModalName(flowState?.narrativeName || DEFAULT_PROMPT_MODAL_NAME);
  }, [flowState, prompts]);

  const handlePromptModalVisibilityChange = (nextIsOpen) => {
    if (!nextIsOpen) {
      if (!shouldPersistPromptChangesRef.current && promptSelectionsSnapshotRef.current) {
        const {
          name,
          selectedAnswers: answersSnapshot,
          transformedAnswersMap: mapSnapshot,
        } = promptSelectionsSnapshotRef.current;
        setPromptModalName(name);
        setSelectedAnswers(answersSnapshot);
        setTransformedAnswersMap(mapSnapshot);
      }

      shouldPersistPromptChangesRef.current = false;
      promptSelectionsSnapshotRef.current = null;
    }

    setIsPromptModalOpen(nextIsOpen);
  };

  const handleSelectPrompts = () => {
    if (!canSelectPrompts) return;
    setBuildError(null);
    promptSelectionsSnapshotRef.current = {
      name: promptModalName,
      selectedAnswers,
      transformedAnswersMap,
    };
    shouldPersistPromptChangesRef.current = false;
    setIsPromptModalOpen(true);
  };

  const handleBuildFromPrompts = async () => {
    if (!canSelectPrompts || isBuildDisabled || isBuilding) return;

    const promptAnswers = buildPromptAnswersPayload(
      transformedAnswersMap,
      flowState?.readOnlyPromptAnswers
    );

    if (!Object.keys(promptAnswers).length) {
      const errorMessage = 'Please complete the required prompts before building your Narrative.';
      setBuildError(errorMessage);
      toast(errorMessage, { status: 'error' });
      return;
    }

    setBuildError(null);
    setIsBuilding(true);

    try {
      const narrative = await createNarrative({
        id: 'dynamic',
        promptAnswers,
        title: promptModalName?.trim() || DEFAULT_PROMPT_MODAL_NAME,
      });

      if (!narrative?.flowId) {
        throw new Error('Narrative build did not return a flow identifier.');
      }

      shouldPersistPromptChangesRef.current = true;
      handlePromptModalVisibilityChange(false);
    } catch (error) {
      const promptErrors = error?.response?.data?.['Prompt Errors'] ?? [];
      const errorMessage =
        promptErrors[0] || 'There was an error building your Narrative. Please try again.';
      setBuildError(errorMessage);
      toast(errorMessage, { status: 'error' });
      console.error('Failed to build Narrative:', error);
    } finally {
      setIsBuilding(false);
    }
  };

  const isBuildDisabled = !areRequiredPromptsFilled(prompts, transformedAnswersMap);
  const hasAllRequiredPrompts = !isBuildDisabled;
  const isViewingRun = Boolean(flowId);
  const isViewingDraft = Boolean(flowState) && !isViewingRun;
  const isNarrativeProcessing =
    Boolean(flowId) &&
    Boolean(narrativeFlow?.status) &&
    !COMPLETED_OR_ERROR_FLOW_STATUSES.includes(narrativeFlow.status);

  let toolbarActionLabel = 'Select Prompts';
  if (isViewingRun) {
    toolbarActionLabel = 'Reprompt';
  } else if (isViewingDraft && hasAllRequiredPrompts) {
    toolbarActionLabel = 'Create Narrative';
  }

  const isToolbarActionDisabled = !canSelectPrompts || isNarrativeProcessing || isBuilding;
  const isToolbarActionLoading = isNarrativeProcessing || isBuilding;
  const draftTopics = useMemo(
    () => (isViewingDraft && flowState ? mapFlowStateComponentsToDrafts(flowState) : []),
    [flowState, isViewingDraft]
  );
  const draftFlowData = useMemo(
    () => (isViewingDraft && flowState ? buildDraftFlowData(flowState) : null),
    [flowState, isViewingDraft]
  );
  const [draftDatasetMap, setDraftDatasetMap] = useState({});
  const componentIds = useMemo(() => {
    if (!draftTopics.length) return [];

    const uniqueIds = new Set();
    draftTopics.forEach((draft) => {
      if (draft?.componentId) {
        uniqueIds.add(draft.componentId);
      }
    });

    return Array.from(uniqueIds);
  }, [draftTopics]);

  const handleToolbarAction = () => {
    if (isBuilding) return;
    if (isViewingDraft && hasAllRequiredPrompts) {
      handleBuildFromPrompts();
      return;
    }

    handleSelectPrompts();
  };

  const preBuildModal = canSelectPrompts ? (
    <PreBuildModal
      activePrompt={activePrompt}
      buildError={buildError}
      getPillTooltip={getPromptPillTooltip}
      getPillValue={getPromptPillValue}
      handleBuild={handleBuildFromPrompts}
      headerIcon={null}
      isBuildDisabled={isBuildDisabled}
      isBuilding={isBuilding}
      isLoading={false}
      isModalOpen={isPromptModalOpen}
      name={promptModalName}
      prompts={prompts}
      routeLabel={DEFAULT_PROMPT_ROUTE_LABEL}
      sameLevelPrompts={[]}
      searchPrompts={[]}
      selectedAnswers={selectedAnswers}
      setActivePrompt={setActivePrompt}
      setIsModalOpen={handlePromptModalVisibilityChange}
      setName={setPromptModalName}
      setSelectedAnswers={setSelectedAnswers}
      setTransformedAnswersMap={setTransformedAnswersMap}
      transformedAnswersMap={transformedAnswersMap}
      transformPromptSelections={transformPromptSelections}
      type="Narrative"
    />
  ) : null;

  useEffect(() => {
    if (!componentIds.length) {
      setDraftDatasetMap({});
      return;
    }

    const controller = new AbortController();
    let isMounted = true;

    const fetchDraftTopics = async () => {
      try {
        const data = await readDraftTopics({ componentIds, signal: controller.signal });
        if (isMounted) {
          setDraftDatasetMap(data);
        }
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
        console.error('Error fetching draft topics:', error);
        if (isMounted) {
          setDraftDatasetMap({});
        }
      }
    };

    fetchDraftTopics();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [componentIds]);

  const enrichedDraftTopics = useMemo(() => {
    if (!draftTopics.length) return [];

    return draftTopics.map((draft) => {
      const apiDraft = draftDatasetMap?.[draft.componentId];
      if (!apiDraft || !apiDraft.dataset) {
        return draft;
      }

      const dataset = {
        ...apiDraft.dataset,
        type: apiDraft.dataset?.type || draft.dataset?.type,
      };
      const resolvedTitle = apiDraft.cleanTitle || apiDraft.title || draft.title;

      return {
        ...draft,
        cleanTitle: apiDraft.cleanTitle || draft.cleanTitle,
        dataset,
        sampleSize: apiDraft.sampleSize ?? draft.sampleSize,
        title: resolvedTitle,
      };
    });
  }, [draftDatasetMap, draftTopics]);

  if (flowId) {
    return (
      <div className={styles['narratives-canvas-container']}>
        <div className={styles['narratives-canvas-content']}>
          <NarrativeFlowView flowId={flowId} hidePageTitle={true} useWindowScroll={false} />
        </div>
        <Footer />
        <CanvasFloatingToolbar
          onSelectPrompts={handleToolbarAction}
          selectPromptsDisabled={isToolbarActionDisabled}
          selectPromptsLabel={toolbarActionLabel}
          selectPromptsLoading={isToolbarActionLoading}
        />
        {preBuildModal}
      </div>
    );
  }

  if (!draftTopics.length) {
    return (
      <div className={styles['narratives-canvas-container']}>
        <div className={styles['narratives-canvas-content']} />
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles['narratives-canvas-container']}>
      <div className={styles['narratives-canvas-content']}>
        <div className={styles['narratives-canvas-draft-view']}>
          {draftFlowData && (
            <NarrativeTitle
              downloadData={EMPTY_DOWNLOAD_MAP}
              flowData={draftFlowData}
              handleReprompt={() => {}}
              setFlow={() => {}}
              showToolbar={false}
            />
          )}
          <div className={styles['narratives-canvas-draft-grid']}>
            {enrichedDraftTopics.map((draft, index) => (
              <NarrativeTopicCard
                initialTopicData={buildDraftTopicData(draft)}
                isDraftCard={true}
                key={draft.id}
                keyIndex={index}
                onPreview={() => {}}
                setDownloadData={() => {}}
                topic={buildTopicShell(draft)}
                useWindowScroll={false}
              />
            ))}
          </div>
        </div>
      </div>
      <Footer />
      <CanvasFloatingToolbar
        onSelectPrompts={handleToolbarAction}
        selectPromptsDisabled={isToolbarActionDisabled}
        selectPromptsLabel={toolbarActionLabel}
        selectPromptsLoading={isToolbarActionLoading}
      />
      {preBuildModal}
    </div>
  );
};

NarrativesCanvas.propTypes = {
  flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flowState: PropTypes.object,
};

export default NarrativesCanvas;
